import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import { ApiErrorPayload } from "types/ApiErrorPayload";

export interface Currency {
  id: number;
  code: string;
  symbol: string;
  name: string;
  type: string;
  scale: number;
  externalSource: string;
  isActive: boolean;
  isSystem: boolean;
  updatedAt: string;
  externalId?: string;
  sourcePayload?: string;
  price?: string;
  priceCurrencyCode?: string;
  priceUpdatedAt?: string;
  createdAt?: string;
  rate?: number | null;
  rateSource?: string | null;
  provider?: string;
  rateFetchedAt?: string | null;
  rateUpdatedAt?: string | null;
}

interface CurrenciesState {
  list: Currency[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isLoading: boolean;
  error?: ApiErrorPayload;
  selectedItems: number[];
  baseCurrency?: string;
}

interface GetCurrenciesPayload {
  limit: number;
  offset: number;
  currency?: string;
  isActive?: number;
  currencyTypes?: string[];
  order: {
    id?: string;
    code?: string;
    type?: string;
    name?: string;
    symbol?: string;
    scale?: string;
    updatedAt?: string;
  };
}

interface UpdateCurrencyPayload {
  currencyId: number;
  isActive: number;
}

interface ManualRatePayload {
  currencyFrom: string;
  baseCurrency: string;
  rate: number;
  expiresInMinutes: number;
}

const initialState: CurrenciesState = {
  list: [],
  isListFetched: false,
  isLoading: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  baseCurrency: undefined,
};

export const getCurrenciesList = createAsyncThunk<
  any,
  GetCurrenciesPayload,
  { rejectValue: ApiErrorPayload }
>("currencies/list", async (data, { rejectWithValue }) => {
  try {
    const result = await instance.post(apiRoutes.paymentCurrenciesList, data);

    return result.data;
  } catch (error: any) {
    const data = error?.data;
    const apiError = data?.error ?? data;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const syncCurrencies = createAsyncThunk<
  boolean,
  void,
  { rejectValue: ApiErrorPayload }
>("currencies/sync", async (_, { rejectWithValue }) => {
  try {
    await instance.post(apiRoutes.paymentCurrenciesSync, {});
    return true;
  } catch (error: any) {
    const data = error?.data;
    const apiError = data?.error ?? data;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const refreshCurrenciesRates = createAsyncThunk<
  boolean,
  void,
  { rejectValue: ApiErrorPayload }
>("currencies/refresh-rates", async (_, { rejectWithValue }) => {
  try {
    await instance.post(apiRoutes.paymentCurrenciesRefreshRates, {});

    return true;
  } catch (error: any) {
    const data = error?.data;
    const apiError = data?.error ?? data;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const setManualCurrencyRate = createAsyncThunk<
  boolean,
  ManualRatePayload,
  { rejectValue: ApiErrorPayload }
>("currencies/manual-rate", async (payload, { rejectWithValue }) => {
  try {
    await instance.post(apiRoutes.paymentCurrenciesManualRate, payload);

    return true;
  } catch (error: any) {
    const data = error?.data;
    const apiError = data?.error ?? data;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const getBaseCurrency = createAsyncThunk<
  { baseCurrency: string },
  void,
  { rejectValue: ApiErrorPayload }
>("currencies/base-currency", async (_, { rejectWithValue }) => {
  try {
    const result = await instance.get(apiRoutes.paymentSystemCurrency);

    return result.data;
  } catch (error: any) {
    const data = error?.data;
    const apiError = data?.error ?? data;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const updateCurrencyActive = createAsyncThunk<
  boolean,
  UpdateCurrencyPayload,
  { rejectValue: ApiErrorPayload }
>(
  "currencies/update-active",
  async ({ currencyId, isActive }, { rejectWithValue }) => {
    try {
      await instance.put(`${apiRoutes.paymentCurrencies}/${currencyId}`, {
        isActive,
      });

      return true;
    } catch (error: any) {
      const data = error?.data;
      const apiError = data?.error ?? data;

      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

export const currenciesSlice = createSlice({
  name: "currencies",
  initialState,
  reducers: {
    handlePaginate: (state, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
    },
    handleSelectItem: (state, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (id) => id !== action.payload,
        );
      } else {
        state.selectedItems.push(action.payload);
      }
    },

    handleSelectAll: (state) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id);
      }
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getCurrenciesList.pending, (state) => {
      state.isLoading = true;
    });

    builder.addCase(getCurrenciesList.fulfilled, (state, action) => {
      
      state.isLoading = false;
      state.isListFetched = true;

      state.baseCurrency = action.payload?.baseCurrency;

      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 0;
      state.totalPages = action.payload?.totalPages ?? 0;
    });

    builder.addCase(getCurrenciesList.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });
    builder.addCase(getBaseCurrency.fulfilled, (state, action) => {
      state.baseCurrency = action.payload?.baseCurrency;
    });
  },
});

export const { handlePaginate, handleSelectItem, handleSelectAll } =
  currenciesSlice.actions;
